import React, { useEffect } from "react";

/**
 * L'APERÇU EN GRAND D'UNE PIÈCE JOINTE — avant de l'envoyer, ou depuis le fil.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * LE JUMEAU DE CELUI DE DASH (`dash/src/Components/ApercuPieceJointe.jsx`). Les deux espaces
 * ne partagent aucun code, et dupliquer soixante lignes coûte moins cher qu'un paquet commun
 * à publier pour ce seul composant — mais ils doivent rester identiques : un aperçu qui se
 * comporte autrement d'un espace à l'autre se remarque immédiatement.
 *
 * CE QU'ELLE RÉPARE. La zone d'attente montrait une vignette de 104 px. Sur une capture
 * d'écran de devoir ou une photo de document, c'est trop petit pour relire ce qu'on envoie —
 * et une fois le message parti, il n'y a pas de retour en arrière. Le clic sur la vignette
 * ouvre donc la pièce en grand, AVANT l'envoi.
 *
 * TROIS NATURES, TROIS TRAITEMENTS : l'image s'agrandit, la vidéo se lit, le son s'écoute.
 * Un PDF n'ouvre rien ici — le navigateur le rend déjà mieux dans son propre onglet.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 *
 * @param {string} url    l'adresse à afficher — locale (`blob:`) avant envoi, distante après
 * @param {string} nom    le nom du fichier, montré sous le média
 * @param {string} genre  "image" | "video" | "audio"
 */
export default function ApercuPieceJointe({ url, nom, genre, onClose }) {
  // Échap ferme, comme partout ailleurs. Le nettoyage évite d'empiler les écouteurs.
  useEffect(() => {
    if (!url) return undefined;
    const surTouche = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [url, onClose]);

  if (!url) return null;

  /* Le clic sur le média lui-même ne referme pas : on vient de l'ouvrir pour le regarder,
     et sur une vidéo le clic sert à mettre en pause. */
  const garder = (e) => e.stopPropagation();

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={nom ? `Aperçu de ${nom}` : "Aperçu de la pièce jointe"}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.88)", zIndex: 3000,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 24, gap: 14, cursor: "zoom-out",
      }}
    >
      {genre === "image" && (
        <img src={url} alt={nom || "Pièce jointe"} onClick={garder}
          style={{
            maxWidth: "min(92vw, 900px)", maxHeight: "80vh", objectFit: "contain",
            borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,.5)", background: "#0f172a",
            cursor: "default",
          }} />
      )}

      {genre === "video" && (
        /* `autoPlay` volontairement absent : on ouvre pour vérifier, pas forcément pour
           lancer le son — et un démarrage automatique surprend plus qu'il ne sert. */
        <video src={url} controls onClick={garder}
          style={{
            maxWidth: "min(92vw, 900px)", maxHeight: "80vh", borderRadius: 12,
            boxShadow: "0 24px 64px rgba(0,0,0,.5)", background: "#000", cursor: "default",
          }} />
      )}

      {genre === "audio" && (
        <div onClick={garder}
          style={{
            background: "#fff", borderRadius: 14, padding: "22px 26px", cursor: "default",
            boxShadow: "0 24px 64px rgba(0,0,0,.5)", width: "min(92vw, 460px)",
          }}>
          <div style={{ fontSize: 34, textAlign: "center", marginBottom: 12 }}>🎧</div>
          <audio controls src={url} style={{ width: "100%" }} />
        </div>
      )}

      {nom && (
        <div style={{
          color: "#e2e8f0", fontSize: 13, fontWeight: 600, maxWidth: "92vw",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{nom}</div>
      )}

      <button onClick={onClose}
        style={{
          position: "fixed", top: 18, right: 22, width: 38, height: 38, borderRadius: "50%",
          border: "none", background: "rgba(255,255,255,.14)", color: "#fff",
          fontSize: 18, cursor: "pointer", lineHeight: 1,
        }}>✕</button>
    </div>
  );
}
